import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import ApiService from '../../common/ApiCalls'

const DEFAULT_SAVE_ERROR =
  'Layout could not be saved. Your changes have been preserved. Please try again.'

export const getLayoutSaveErrorMessage = (error) => {
  const responseData = error?.response?.data
  const backendMessages = Array.isArray(responseData?.errors)
    ? responseData.errors
      .map(backendError => backendError?.message)
      .filter(message => typeof message === 'string' && message.trim())
    : []

  if (backendMessages.length > 0) {
    return [
      'Layout could not be saved.',
      ...backendMessages,
      'Your changes have been preserved.'
    ].join('\n')
  }

  const responseMessage = typeof responseData?.message === 'string'
    ? responseData.message
    : typeof responseData === 'string'
      ? responseData
      : null
  const message = responseMessage || error?.message

  if (typeof message === 'string' && message.trim()) {
    return [
      'Layout could not be saved.',
      message,
      'Your changes have been preserved.'
    ].join('\n')
  }

  return DEFAULT_SAVE_ERROR
}

const useLayoutEditor = (layoutId, objectTypeParam) => {
  const navigate = useNavigate()
  const [layout, setLayout] = useState(null)
  const [isLoading, setIsLoading] = useState(false)
  const [saveFeedback, setSaveFeedback] = useState({
    isOpen: false,
    severity: 'success',
    message: ''
  })
  const [industries, setIndustries] = useState([])
  const [objectType, setObjectType] = useState(objectTypeParam)
  const [isDefaultLayout, setIsDefaultLayout] = useState(false)

  useEffect(() => {
    fetchIndustries()
    if (layoutId) {
      setIsLoading(true)
      fetchLayout(layoutId)
    } else {
      setLayout({
        name: '',
        description: '',
        subject_type: objectTypeParam === 'entity' ? 'entity' : 'asset',
        object_type: objectTypeParam,
        industry_ids: [],
        is_default: false,
        cards: [],
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [layoutId])

  const fetchIndustries = async () => {
    try {
      const response = await ApiService().getIndustries()
      setIndustries(response)
    } catch (error) {
      console.error('Error fetching industries:', error)
    }
  }

  const fetchLayout = async (layoutId) => {
    setIsLoading(true)
    try {
      const response = await ApiService().getLayout(objectType, layoutId)
      const fetchedLayout = response[0]
  
      const allFieldIds = []
      fetchedLayout.cards.forEach(card => {
        card.items.forEach(item => {
          if (item.item_type === 'field' && item.field_id) {
            allFieldIds.push(item.field_id)
          }
        })
      })
  
      let fieldMap = {}
      if (allFieldIds.length > 0) {
        const fieldsData = await ApiService().getFields(allFieldIds.join('&ids[]='))
        fieldsData.forEach(field => {
          fieldMap[field.id] = {
            display_label: field.label,
            display_field_type: field.field_type
          }
        })
      }
  
      const updatedCards = fetchedLayout.cards.map(card => {
        const updatedItems = card.items.map(item => {
          if (item.item_type === 'field' && fieldMap[item.field_id]) {
            return {
              ...item,
              display_label: fieldMap[item.field_id].display_label,
              display_field_type: fieldMap[item.field_id].display_field_type
            }
          }
          return item
        })
        return { ...card, items: updatedItems }
      })
  
      const updatedLayout = { ...fetchedLayout, cards: updatedCards }
      setLayout(updatedLayout)
      setObjectType(updatedLayout.object_type)
      setIsDefaultLayout(updatedLayout.is_default)
    } catch (error) {
      console.error('Error fetching layout:', error)
    } finally {
      setIsLoading(false)
    }
  }
  

  const handleSave = async (layoutToSave = layout) => {
    setIsLoading(true)
    setSaveFeedback(previousFeedback => ({
      ...previousFeedback,
      isOpen: false
    }))
    try {
      if (layoutId) {
        const trimmedLayout = trimLayoutUpdate(layoutToSave)
        await ApiService().updateLayout(layoutId, trimmedLayout)
      } else {
        await ApiService().createLayout(layoutToSave)
      }
      setSaveFeedback({
        isOpen: true,
        severity: 'success',
        message: 'Layout saved successfully'
      })
      navigate('/adminDashboard')
    } catch (error) {
      console.error('Error saving layout:', error)
      setSaveFeedback({
        isOpen: true,
        severity: 'error',
        message: getLayoutSaveErrorMessage(error)
      })
    } finally {
      setIsLoading(false)
    }
  }

  const trimLayoutUpdate = (layout) => {
    return {
      archived_at: layout.archived_at,
      cards: layout.cards,
      description: layout.description,
      industry_ids: layout.industry_ids,
      is_default: layout.is_default,
      name: layout.name,
      object_type: layout.object_type,
      subject_type: layout.subject_type,
    }
  }

  const handleCancel = () => {
    navigate('/adminDashboard')
  }

  const addCard = () => {
    setLayout({
      ...layout,
      cards: [...layout.cards, { title: '', description: '', items: [] }],
    })
  }

  const deleteCard = (index) => {
    const newCards = [...layout.cards]
    newCards.splice(index, 1)
    setLayout({ ...layout, cards: newCards })
  }

  const moveCard = (fromIndex, toIndex) => {
    const newCards = [...layout.cards]
    const [movedCard] = newCards.splice(fromIndex, 1)
    newCards.splice(toIndex, 0, movedCard)
    setLayout({ ...layout, cards: newCards })
  }

  const addField = (cardIndex, field) => {
    const newCards = [...layout.cards]
    newCards[cardIndex].items.push({
      item_type: 'field',
      field_id: field.id
    })
    setLayout({ ...layout, cards: newCards })
  }

  const deleteField = (cardIndex, fieldIndex) => {
    const newCards = [...layout.cards]
    newCards[cardIndex].items.splice(fieldIndex, 1)
    setLayout({ ...layout, cards: newCards })
  }

  const moveField = (cardIndex, fromIndex, toIndex) => {
    const newCards = [...layout.cards]
    const items = newCards[cardIndex].items
    const [movedField] = items.splice(fromIndex, 1)
    items.splice(toIndex, 0, movedField)
    newCards[cardIndex].items = items
    setLayout({ ...layout, cards: newCards })
  }

  return {
    layout,
    setLayout,
    isLoading,
    saveFeedback,
    setSaveFeedback,
    handleSave,
    handleCancel,
    addCard,
    deleteCard,
    moveCard,
    addField,
    deleteField,
    moveField,
    industries,
    objectType,
    setObjectType,
    isDefaultLayout,
    setIsDefaultLayout,
  }
}

export default useLayoutEditor
